/**
 * Per-brand branding + host contact data, bootstrapped server-side by the tenant
 * layout as a `<script id="branding-bootstrap" type="application/json">` payload
 * (name, domain, logo, favicon, palette, platform flag, and the clinic contact
 * data/service area which are only sent for brand subdomains). Falls back to the
 * Vite build-time env values when the payload is absent (apex/platform host).
 */
interface Branding {
  siteName: string;
  domain: string;
  logoUrl: string;
  faviconUrl: string;
  color1: string | null;
  color2: string | null;
  isPlatform: boolean;
  /**
   * Path-based tenant prefix (`/<slug>`), set by the middleware when the brand
   * is reached through the platform apex. Null on a subdomain and on the apex.
   * The SPA router uses it as its history base so internal links stay inside
   * the brand.
   */
  pathPrefix: string | null;
  phone: string | null;
  address: string | null;
  serviceArea: string | null;
}

const STATIC_LOGO = '/images/logo.jpeg';
const STATIC_FAVICON = '/images/logo.ico';

export function loadBranding(): Branding {
  const fallback: Branding = {
    siteName: import.meta.env.VITE_SITE_NAME || import.meta.env.VITE_SITE_NAME_PIE_VIVO,
    domain: import.meta.env.VITE_SITE_DOMAIN || '',
    logoUrl: STATIC_LOGO,
    faviconUrl: STATIC_FAVICON,
    color1: null,
    color2: null,
    isPlatform: true,
    pathPrefix: null,
    phone: null,
    address: null,
    serviceArea: null,
  };
  const node = document.getElementById('branding-bootstrap');
  if (!node?.textContent) return fallback;
  try {
    const parsed = JSON.parse(node.textContent) as Partial<Branding>;
    return {
      siteName: parsed.siteName || fallback.siteName,
      domain: parsed.domain || fallback.domain,
      logoUrl: parsed.logoUrl || fallback.logoUrl,
      faviconUrl: parsed.faviconUrl || fallback.faviconUrl,
      color1: parsed.color1 || fallback.color1,
      color2: parsed.color2 || fallback.color2,
      isPlatform: parsed.isPlatform ?? fallback.isPlatform,
      pathPrefix: typeof parsed.pathPrefix === 'string' ? parsed.pathPrefix : fallback.pathPrefix,
      phone: parsed.phone ?? fallback.phone,
      address: parsed.address ?? fallback.address,
      serviceArea: parsed.serviceArea ?? fallback.serviceArea,
    };
  } catch {
    return fallback;
  }
}
